"use client";

import { ChevronLeft, ChevronRight } from "lucide-react";
import { useReducedMotion } from "motion/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { cn } from "../lib/utils";

export const InfiniteMovingCards = ({
	items,
	direction = "left",
	speed = "fast",
	pauseOnHover = true,
	className,
}: {
	items: {
		logo: string;
		name: string;
		quote?: string;
		title?: string;
	}[];
	direction?: "left" | "right";
	speed?: "fast" | "normal" | "slow";
	pauseOnHover?: boolean;
	className?: string;
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const scrollerRef = useRef<HTMLUListElement>(null);
	const [start, setStart] = useState(false);
	const [isManualScrolling, setIsManualScrolling] = useState(false);
	const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
	const shouldReduceMotion = useReducedMotion();
	const manualScrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
		null,
	);
	const hasDuplicatedRef = useRef(false);
	const prevItemsLengthRef = useRef(items.length);

	// Reset duplication state when items change (CMS reactivity fix)
	useEffect(() => {
		if (prevItemsLengthRef.current !== items.length) {
			hasDuplicatedRef.current = false;
			prevItemsLengthRef.current = items.length;
			// Re-trigger animation setup - clear existing duplicates
			if (scrollerRef.current) {
				const originalCount = items.length;
				while (scrollerRef.current.children.length > originalCount) {
					scrollerRef.current.removeChild(
						scrollerRef.current.lastChild as Node,
					);
				}
			}
		}
	}, [items.length]);

	// Get actual card width dynamically (responsive: 280px mobile, 320px desktop + 16px gap)
	const getCardWidth = useCallback(() => {
		if (!scrollerRef.current?.firstElementChild) {
			// Fallback: check if we're on mobile
			return window.innerWidth < 768 ? 296 : 336; // 280+16 or 320+16
		}
		const firstCard = scrollerRef.current.firstElementChild as HTMLElement;
		const gap = 16; // gap-4 = 16px
		return firstCard.offsetWidth + gap;
	}, []);

	// Manual scroll navigation - works by scrolling the container
	const scrollCarousel = useCallback(
		(direction: "left" | "right") => {
			if (!containerRef.current) return;

			// Pause auto-animation during manual scroll
			setIsManualScrolling(true);

			// Clear existing timeout
			if (manualScrollTimeoutRef.current) {
				clearTimeout(manualScrollTimeoutRef.current);
			}

			// Scroll the container using dynamic card width
			const cardWidth = getCardWidth();
			const scrollAmount = direction === "left" ? -cardWidth : cardWidth;
			containerRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });

			// Resume auto-scroll after 4 seconds of inactivity
			manualScrollTimeoutRef.current = setTimeout(() => {
				setIsManualScrolling(false);
			}, 4000);
		},
		[getCardWidth],
	);

	const getDirection = useCallback(() => {
		if (containerRef.current) {
			containerRef.current.style.setProperty(
				"--animation-direction",
				direction === "left" ? "forwards" : "reverse",
			);
		}
	}, [direction]);

	const getSpeed = useCallback(() => {
		if (containerRef.current) {
			let duration = "80s";
			if (speed === "fast") {
				duration = "20s";
			} else if (speed === "normal") {
				duration = "40s";
			}
			containerRef.current.style.setProperty("--animation-duration", duration);
		}
	}, [speed]);

	const addAnimation = useCallback(() => {
		if (containerRef.current && scrollerRef.current) {
			// Only duplicate once to prevent DOM bloat on re-renders
			if (!hasDuplicatedRef.current) {
				const scrollerContent = Array.from(scrollerRef.current.children);
				for (const item of scrollerContent) {
					const duplicatedItem = item.cloneNode(true);
					scrollerRef.current?.appendChild(duplicatedItem);
				}
				hasDuplicatedRef.current = true;
			}
			getDirection();
			getSpeed();
			setStart(true);
		}
	}, [getDirection, getSpeed]);

	useEffect(() => {
		addAnimation();
		return () => {
			if (manualScrollTimeoutRef.current) {
				clearTimeout(manualScrollTimeoutRef.current);
			}
		};
	}, [addAnimation]);

	// Keyboard navigation
	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			if (focusedIndex === null) {
				return;
			}

			if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
				e.preventDefault();
				const newIndex =
					e.key === "ArrowLeft"
						? (focusedIndex - 1 + items.length) % items.length
						: (focusedIndex + 1) % items.length;
				setFocusedIndex(newIndex);

				// Focus the new button element
				const buttonElement = document.querySelector(
					`button[data-carousel-index="${newIndex}"]`,
				) as HTMLButtonElement;
				buttonElement?.focus();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		return () => window.removeEventListener("keydown", handleKeyDown);
	}, [focusedIndex, items.length]);

	return (
		<div className="group/carousel relative">
			{/* Left navigation arrow - visible on mobile, hover on desktop */}
			{/* WCAG 2.5.5: Touch target minimum 44px (h-11 w-11 = 44px) */}
			<button
				aria-label="Scroll carousel left"
				className="absolute left-2 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 shadow-md backdrop-blur-sm transition-all hover:bg-accent hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring opacity-70 md:opacity-0 md:group-hover/carousel:opacity-100 md:focus-visible:opacity-100"
				onClick={() => scrollCarousel("left")}
				type="button"
			>
				<ChevronLeft className="h-5 w-5 text-foreground" />
			</button>

			{/* Right navigation arrow - visible on mobile, hover on desktop */}
			{/* WCAG 2.5.5: Touch target minimum 44px (h-11 w-11 = 44px) */}
			<button
				aria-label="Scroll carousel right"
				className="absolute right-2 top-1/2 z-30 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-border bg-background/90 shadow-md backdrop-blur-sm transition-all hover:bg-accent hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring opacity-70 md:opacity-0 md:group-hover/carousel:opacity-100 md:focus-visible:opacity-100"
				onClick={() => scrollCarousel("right")}
				type="button"
			>
				<ChevronRight className="h-5 w-5 text-foreground" />
			</button>

			<div
				className={cn(
					"scroller relative z-20 max-w-7xl overflow-x-auto scrollbar-hide [mask-image:linear-gradient(to_right,transparent,white_10%,white_90%,transparent)]",
					// Enable scroll-snap for touch/drag
					"snap-x snap-mandatory scroll-smooth",
					className,
				)}
				ref={containerRef}
				// Pause animation on touch/scroll
				onScroll={() => {
					if (!isManualScrolling) {
						setIsManualScrolling(true);
						if (manualScrollTimeoutRef.current) {
							clearTimeout(manualScrollTimeoutRef.current);
						}
						manualScrollTimeoutRef.current = setTimeout(() => {
							setIsManualScrolling(false);
						}, 4000);
					}
				}}
			>
				<ul
					className={cn(
						"flex w-max min-w-full shrink-0 flex-nowrap gap-4 py-4",
						// Only animate when not manually scrolling and motion is enabled
						start &&
							!shouldReduceMotion &&
							!isManualScrolling &&
							"animate-scroll",
						pauseOnHover &&
							!shouldReduceMotion &&
							"hover:[animation-play-state:paused]",
					)}
					ref={scrollerRef}
				>
					{items.map((item, idx) => (
						<li className="shrink-0 snap-start" key={item.name}>
							<button
								aria-label={`${item.name}${item.quote ? " - View testimonial" : ""}`}
								className={cn(
									"group relative block rounded-xl border border-border bg-card/50 text-left transition-shadow duration-300 ease-in-out",
									"aspect-[2/1] w-[280px] md:w-[320px]",
									item.quote && "cursor-pointer hover:shadow-lg",
									focusedIndex === idx && "ring-2 ring-primary ring-offset-2",
								)}
								data-carousel-index={idx}
								onBlur={() => setFocusedIndex(null)}
								onFocus={() => setFocusedIndex(idx)}
								type="button"
							>
								{/* Logo - always visible, styled as smaller rectangle */}
								<div
									className={cn(
										"flex h-full w-full items-center justify-center rounded-xl bg-gradient-to-br from-card to-muted p-6",
										item.quote &&
											"transition-all duration-300 ease-in-out group-hover:invisible group-hover:opacity-0 group-focus-within:invisible group-focus-within:opacity-0",
									)}
								>
									<img
										alt={`${item.name} logo`}
										className="max-h-[80px] max-w-[180px] object-contain brightness-95 filter md:max-h-[90px] md:max-w-[200px] dark:brightness-110"
										height={90}
										loading="lazy"
										src={item.logo}
										width={200}
									/>
								</div>

								{/* Testimonial content - visible on hover AND focus (WCAG 2.1.1/1.4.13) */}
								{item.quote && (
									<blockquote className="invisible absolute inset-0 rounded-xl bg-popover/96 p-4 opacity-0 backdrop-blur-sm transition-all duration-300 ease-in-out group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
										{/* Header with logo and company info in top-left */}
										<div className="mb-3 flex items-start gap-3">
											{/* Company logo - small box in top-left */}
											<div className="shrink-0">
												<img
													alt={`${item.name} logo`}
													className="h-8 w-8 object-contain brightness-95 filter md:h-10 md:w-10 dark:brightness-110"
													height={40}
													loading="lazy"
													src={item.logo}
													width={40}
												/>
											</div>
											{/* Company info next to logo */}
											<div className="min-w-0 flex-1">
												<div className="truncate font-semibold text-foreground text-sm">
													{item.name}
												</div>
												{item.title && (
													<div className="line-clamp-1 text-muted-foreground text-xs">
														{item.title}
													</div>
												)}
											</div>
										</div>

										{/* Quote */}
										<div className="line-clamp-4 text-muted-foreground text-xs leading-relaxed">
											"{item.quote}"
										</div>
									</blockquote>
								)}
							</button>
						</li>
					))}
				</ul>
			</div>
		</div>
	);
};

export default InfiniteMovingCards;
